/**
 * AvailabilitySection — Settings → Availability card.
 *
 * Two panels:
 *   • Default weekly hours (Sun–Sat): toggle + start/end time per day
 *   • Weekly calendar view: click a date to override that specific day
 *     (either a day-off, or custom hours). Overrides key on YYYY-MM-DD
 *     in the user's local calendar.
 *
 * Persists to PUT /api/users/me/availability on every change (debounced
 * via a small queue). Reads via GET on mount.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { CalendarClock, ChevronLeft, ChevronRight, Loader2, Clock } from 'lucide-react';

const DAYS = [
  { key: 'sun', label: 'Sunday',    short: 'Sun' },
  { key: 'mon', label: 'Monday',    short: 'Mon' },
  { key: 'tue', label: 'Tuesday',   short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday',  short: 'Thu' },
  { key: 'fri', label: 'Friday',    short: 'Fri' },
  { key: 'sat', label: 'Saturday',  short: 'Sat' },
];

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const startOfWeekSun = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const friendlyRange = (start, end, enabled) => {
  if (!enabled) return 'Unavailable';
  return `${fmt12(start)} – ${fmt12(end)}`;
};

const fmt12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

// Resolve effective hours for a given calendar date against the
// current availability doc (overrides win over defaults).
const resolveDay = (avail, date) => {
  if (!avail) return null;
  const key = iso(date);
  const dayKey = DAYS[date.getDay()].key;
  if (avail.overrides && avail.overrides[key]) {
    const o = avail.overrides[key];
    return {
      enabled: !!o.enabled,
      start: o.start || avail.default_hours[dayKey].start,
      end: o.end || avail.default_hours[dayKey].end,
      isOverride: true,
    };
  }
  const d = avail.default_hours[dayKey];
  return { enabled: !!d.enabled, start: d.start, end: d.end, isOverride: false };
};

const AvailabilitySection = ({ api }) => {
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekStart, setWeekStart] = useState(() => startOfWeekSun(new Date()));
  const [editDate, setEditDate] = useState(null); // Date being overridden

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/users/me/availability');
        setAvail(r.data);
      } catch (e) {
        toast.error('Failed to load availability');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  // Single save helper — caller builds the full new doc then we PUT.
  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      const r = await api.put('/users/me/availability', patch);
      setAvail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  }, [api]);

  const updateDefault = (dayKey, patch) => {
    if (!avail) return;
    const merged = { ...avail.default_hours[dayKey], ...patch };
    // Optimistic UI update so toggles/time changes feel immediate.
    setAvail({
      ...avail,
      default_hours: { ...avail.default_hours, [dayKey]: merged },
    });
    persist({ default_hours: { [dayKey]: merged } });
  };

  const saveOverride = (dateIso, entry) => {
    if (!avail) return;
    const next = { ...(avail.overrides || {}) };
    if (entry === null) {
      delete next[dateIso];
    } else {
      next[dateIso] = entry;
    }
    setAvail({ ...avail, overrides: next });
    persist({ overrides: next });
  };

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  if (loading) {
    return (
      <Card data-testid="availability-card">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!avail) return null;

  return (
    <Card data-testid="availability-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="w-4 h-4" />
          Availability
          {saving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
        </CardTitle>
        <CardDescription>
          Set your default hours for each day of the week. Use the weekly
          calendar below to change a specific day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Default weekly hours */}
        <div className="space-y-2" data-testid="availability-defaults">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
            Default hours
          </p>
          <div className="space-y-2">
            {DAYS.map((d) => {
              const dh = avail.default_hours[d.key];
              return (
                <div
                  key={d.key}
                  className="flex items-center gap-2 py-1.5"
                  data-testid={`availability-default-${d.key}`}
                >
                  <div className="w-16 text-sm font-medium">{d.short}</div>
                  <Switch
                    checked={dh.enabled}
                    onCheckedChange={(v) => updateDefault(d.key, { enabled: v })}
                    data-testid={`availability-default-toggle-${d.key}`}
                  />
                  {dh.enabled ? (
                    <div className="flex items-center gap-1 flex-1">
                      <Input
                        type="time"
                        value={dh.start}
                        onChange={(e) => updateDefault(d.key, { start: e.target.value })}
                        className="h-8 w-24 text-xs"
                        data-testid={`availability-default-start-${d.key}`}
                      />
                      <span className="text-xs text-muted-foreground">–</span>
                      <Input
                        type="time"
                        value={dh.end}
                        onChange={(e) => updateDefault(d.key, { end: e.target.value })}
                        className="h-8 w-24 text-xs"
                        data-testid={`availability-default-end-${d.key}`}
                      />
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground flex-1">Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Weekly calendar — override per date */}
        <div className="space-y-2 pt-2 border-t border-border/50" data-testid="availability-week">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
              This week
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setWeekStart(addDays(weekStart, -7))}
                className="p-1 rounded hover:bg-muted"
                data-testid="availability-week-prev"
                aria-label="Previous week"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-medium px-2 min-w-[8rem] text-center">
                {weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} –{' '}
                {addDays(weekStart, 6).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
              <button
                onClick={() => setWeekStart(addDays(weekStart, 7))}
                className="p-1 rounded hover:bg-muted"
                data-testid="availability-week-next"
                aria-label="Next week"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            {weekDays.map((d) => {
              const resolved = resolveDay(avail, d);
              const dateIso = iso(d);
              const isToday = iso(new Date()) === dateIso;
              return (
                <button
                  key={dateIso}
                  onClick={() => setEditDate(d)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors border ${
                    resolved.isOverride
                      ? 'bg-[#586D52]/5 border-[#586D52]/40'
                      : 'bg-muted/30 border-transparent hover:bg-muted/60'
                  }`}
                  data-testid={`availability-day-${dateIso}`}
                >
                  <div className="flex flex-col items-center w-10">
                    <span className="text-[10px] uppercase text-muted-foreground">
                      {DAYS[d.getDay()].short}
                    </span>
                    <span className={`text-lg font-semibold ${isToday ? 'text-[#586D52]' : ''}`}>
                      {d.getDate()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${resolved.enabled ? '' : 'text-muted-foreground'}`}>
                      {friendlyRange(resolved.start, resolved.end, resolved.enabled)}
                    </p>
                    {resolved.isOverride && (
                      <p className="text-[10px] uppercase tracking-wide text-[#586D52] font-semibold">
                        Custom
                      </p>
                    )}
                  </div>
                  <Clock className="w-4 h-4 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>

      {editDate && (
        <OverrideEditor
          date={editDate}
          resolved={resolveDay(avail, editDate)}
          existingOverride={avail.overrides?.[iso(editDate)] || null}
          onClose={() => setEditDate(null)}
          onSave={(entry) => {
            saveOverride(iso(editDate), entry);
            setEditDate(null);
          }}
          onClear={() => {
            saveOverride(iso(editDate), null);
            setEditDate(null);
          }}
        />
      )}
    </Card>
  );
};

const OverrideEditor = ({ date, resolved, existingOverride, onClose, onSave, onClear }) => {
  const [enabled, setEnabled] = useState(resolved.enabled);
  const [start, setStart] = useState(resolved.start);
  const [end, setEnd] = useState(resolved.end);

  const handleSave = () => {
    if (enabled && start >= end) {
      toast.error('Start time must be before end time');
      return;
    }
    onSave({
      enabled,
      start: enabled ? start : null,
      end: enabled ? end : null,
    });
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="availability-override-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            {date.toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="override-enabled" className="text-sm">Available</Label>
            <Switch
              id="override-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="availability-override-toggle"
            />
          </div>
          {enabled && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Custom hours for this day</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="availability-override-start"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <Input
                  type="time"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="availability-override-end"
                />
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            {existingOverride && (
              <Button
                variant="outline"
                onClick={onClear}
                className="flex-1"
                data-testid="availability-override-clear"
              >
                Reset to default
              </Button>
            )}
            <Button
              onClick={handleSave}
              className="flex-1 bg-[#586D52] hover:bg-[#4a5d44] text-white"
              data-testid="availability-override-save"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvailabilitySection;
